import {
  ChangeDetectionStrategy,
  Component,
  Input,
  type OnDestroy,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { type IPricingRuleItem } from '@principle-theorem/principle-core/interfaces';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  ReplaySubject,
  Subject,
} from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { type PricingRuleFormGroup } from './pricing-rule.formgroup';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { isUndefined } from 'lodash';

@Component({
  selector: 'pr-pricing-rule',
  templateUrl: './pricing-rule.component.html',
  styleUrls: ['./pricing-rule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PricingRuleComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  form$ = new ReplaySubject<PricingRuleFormGroup>(1);
  min$ = new ReplaySubject<number>(1);
  disabled$ = new BehaviorSubject<boolean>(false);
  rangeEnabled$ = new ReplaySubject<boolean>(1);
  isVariable$ = new ReplaySubject<boolean>(1);
  defaultRuleItem$ = new ReplaySubject<IPricingRuleItem | undefined>(1);
  ruleIndex$ = new ReplaySubject<number>(1);
  labelPlaceholder$: Observable<string>;

  constructor() {
    combineLatest([this.form$, this.disabled$])
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(([form, disabled]) => {
        if (disabled) {
          form.controls.endUnit.disable();
          return;
        }
        form.controls.endUnit.enable();
      });

    combineLatest([this.form$, this.min$])
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(([form, min]) =>
        form.controls.endUnit.setValidators(Validators.min(min))
      );

    this.labelPlaceholder$ = this.ruleIndex$.pipe(
      map((index) => `Option ${index + 1}`)
    );
  }

  @Input()
  set defaultRuleItem(defaultRuleItem: IPricingRuleItem | undefined) {
    if (defaultRuleItem) {
      this.defaultRuleItem$.next(defaultRuleItem);
    }
  }

  @Input()
  set rangeEnabled(rangeEnabled: BooleanInput) {
    this.rangeEnabled$.next(coerceBooleanProperty(rangeEnabled));
  }

  @Input()
  set isVariable(isVariable: BooleanInput) {
    this.isVariable$.next(coerceBooleanProperty(isVariable));
  }

  @Input()
  set ruleIndex(ruleIndex: number) {
    if (!isUndefined(ruleIndex)) {
      this.ruleIndex$.next(ruleIndex);
    }
  }

  @Input()
  set disabled(disabled: boolean) {
    this.disabled$.next(disabled);
  }

  @Input()
  set min(min: number) {
    if (min) {
      this.min$.next(min);
    }
  }

  @Input()
  set form(form: PricingRuleFormGroup) {
    if (form) {
      this.form$.next(form);
    }
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
